import Vue from 'vue';
import VueRouter from 'vue-router';
import Meta from 'vue-meta';
import NodePath from '../utils/node-path';
import { PERSISTEDSTATE_KEY, STORAGE_KEY_KEEP_LOGON, STORAGE_KEY_KEEP_TOKEN } from '@/constants';
import store from '@/store';
import { fetch } from '@/store/prefetch';

Vue.use(VueRouter);
Vue.use(Meta);

export default class Router {
  constructor(routeItems, store) {
    this.router = this.getInstance(routeItems || []);
    this.$store = store;
  }

  // vue router 객체 반환
  // 1. vue 객체 생성 당시 주입된 route 정보가 있을 경우 route 정보 추가 작업
  // 2. vue router navigation-gard 설정
  async getInstance(routeItems) {
    const router = new VueRouter({
      mode: 'history',
      base: process.env.BASE_URL,
      routes: await this.routes(routeItems),
    });
    this.beforeEach(router);
    // BEGIN::Added.by.KWShin.2025.10.23.
    this.afterEach(router);
    // FINISH::Added.by.KWShin.2025.10.23.

    return router;
  }

  // vue 객체 생성 당시 주입된 route 정보가 있을 경우 route 정보 추가 작업
  async routes(routeItems) {
    // add route item in project
    const context = require.context('../routes', true, /[a-z0-9-_]+\.js$/i);
    let routes = context.keys().reduce((acc, path) => {
      acc.push(...context(path).default);
      return acc;
    }, []);

    // add route item from plug-in
    if (routeItems.length) {
      routes = await Promise.all(routeItems).then(resolves => {
        return resolves.reduce((acc, item) => acc.concat(item.default), routes);
      });
    }
    return routes;
  }

  // BEGIN::Added.by.KWShin.2025.10.23.
  /**
   * 로그인 과정 없이 자동로그인 되는 기능을 만들었다.
   * (물론, 자동로그인은 서버 설정이 자동로그인 기능을 사용하게끔 되어 있고
   *  접속하는 클라이언트의 IP가 서버에 등록되어 있는 경우로 제한된다.)
   * 서버에서 자동로그인을 허용한 경우 서버에 사용자 컨텍스트를 디폴트로 'admin'으로 설정되게 만들었다.
   * 그런데 문제가 서버는 'admin'으로 인식을 하는데 Vue 단(즉 클라이언트 단)은
   * 사용자를 'admin'으로 인식할 방법이 없다.
   * 방법을 고민한 끝에 여기서 'common/user' Store의 Action 'fetchUser()'를 호출하는 방법을 찾았다.
   * 'fetchUser()'에서 호출하는 api '/user'는 서버의 사용자 컨텍스트를 기반으로 사용자 정보를 리턴하여 준다.
   * 그래서 해결책이 된다.
   */
  afterEach(router) {
    router.afterEach(to => {
      if (to.query.awt == 'T') {
        store._actions['common/user/fetchUser'][0]();
      }
    });
  }
  // FINISH::Added.by.KWShin.2025.10.23.

  // vue router before process
  beforeEach(router) {
    router.beforeEach((to, from, next) => this.navigationGard(router, to, from, next));
  }

  // vue router navigation-gard 설정
  async navigationGard(router, to, from, next) {
    // sessionStorage 내의 vuex 이름으로 저장된 정보를 참조
    const auth = (JSON.parse(window.sessionStorage.getItem(PERSISTEDSTATE_KEY.AUTH) || '{}').common || {}).auth || {};
    // route 정의 내용 중 exceptAuth (인증 예외) 가 아닌 경우 auth 체크 여부를 판단
    const requireAuth = to.matched.some(routeInfo => !routeInfo.meta.exceptAuth);
    // auth 체크 필요
    if (requireAuth) {
      if (auth.token) {
        // 접근제한 메뉴에 접근 시 로그인 페이지로 옮겨준다.
        if ((await this.isAllowedMenu(to)) == false) router.push({ path: '/login' });
        // token 발급 정보가 있을 경우 : to path 이동
        this.gotoNextWithToken(router, to, from, next);
      } else {
        if (to.query.awt == 'T') {
          // URL Parameter로 awt=true가 넘어오면 token이 없어도 정상적으로 이동한다.
          // token이 올라가지 않아도, 서버의 설정과 요청의 IP를 서버에서 체크하여 정상적으로 처리해준다.
          // 단, 이 때는 접근제한메뉴에 접근하는 지 여부를 체크하지 않는다.
          this.gotoNextWithToken(router, to, from, next);
        } else {
          // access token이 없는데 로그온 유지 기능이 켜져 있는 경우,
          // 로그온 유지 기능을 켤 때 localStorage에 보관해 놓았던 access token을 store에 다시 넣어주고
          // 화면이 정상적으로 동작할 수 있도록 사용자/메뉴 정보를 캐싱한다.
          const keepLogon = window.localStorage.getItem(STORAGE_KEY_KEEP_LOGON) === 'true' ? true : false;
          if (keepLogon) {
            store._actions['common/auth/authorize'][0]({
              status: 200,
              token: window.localStorage.getItem(STORAGE_KEY_KEEP_TOKEN),
            });
            // BEGIN::Modified.by.KWShin.2026.09.10.
            // fetchMenus()는 store의 username으로 메뉴접근권한을 조회하므로
            // fetchUser()가 끝난 뒤에 호출되어야 한다.
            await store._actions['common/user/fetchUser'][0]();
            await store._actions['common/menu/fetchMenus'][0]();
            // FINISH::Modified.by.KWShin.2026.09.10.

            // 접근제한 메뉴에 접근 시 로그인 페이지로 옮겨준다.
            if ((await this.isAllowedMenu(to)) == false) router.push({ path: '/login' });

            this.gotoNextWithToken(router, to, from, next);
          } else {
            router.push({ path: '/login' });
          }
        }
      }
    }
    // auth 체크 불필요
    else {
      // route 정의 내용 중 로그인 폼 인 경우 판단
      const isLoginForm = to.matched.some(routeInfo => routeInfo.meta.isLoginForm);
      // 로그인 폼 일 경우
      if (isLoginForm) {
        // token 발급 정보가 있을 경우 : to path or root path 이동
        if (auth.token) router.push({ path: from.path || '/' });
        // token 발급 정보가 없을 경우 : to path (/login) 이동
        else next();
      }
      // 로그인 폼이 아닐 경우 : to path 이동
      else next();
    }
  }

  gotoNextWithToken(router, to, from, next) {
    // fetchs server info
    fetch(this.$store).then(() => {
      const path = path => Object.values(to.params).reduce((acc, item) => acc.replace(new RegExp(`\\/${item}$`), ''), path); // params 정보를 제외한 path
      const pathByKey = path => Object.keys(to.params).reduce((acc, item) => acc.replace(new RegExp(`\\/:${item}$`), ''), path); // params 정보를 제외한 path
      const options = router.options.routes.find(item => pathByKey(item.path) == path(to.path));
      const components = (options || {}).components || {};
      to.meta.path = path(to.path);
      to.meta.singleComponent = Object.keys(components).length == 1;
      to.meta.menu = this.currentMenu(to); // 메뉴 정보
      to.meta.from = from; // 이전 route 정보
      next();
    });
  }

  // 현재 사용자의 권한에 해당하는 메뉴정보를 바탕으로 현재 url path 기준 menu 정보를 반환한다.
  currentMenu(route) {
    const menu = this.$store.state.common.menu;
    // BEGIN::Modified.by.KWShin.2026.01.06.
    // const nodePath = new NodePath(menu.realtime.concat(menu.analstatic), { key: 'url', value: route.meta.path }).find(false);
    const nodePath = new NodePath(menu.realtime.concat(menu.analstatic, menu.extraordinaries), { key: 'url', value: route.meta.path }).find(false);
    // FINISH::Modified.by.KWShin.2026.01.06.
    const node = nodePath.nodes[0] || {};
    return {
      icon: node.icon,
      messageCode: node.messageCode,
    };
  }

  /**
   * Added.by.KWShin.2026.07.20.
   * 접근허용메뉴에 들어 있는 PATH인지 체크하여 아니면 접근 거부하도록 false를 리턴.
   */
  async isAllowedMenu(routeTo) {
    if (store.state.common.menu.realtime.length == 0) {
      // 아직 메뉴 정보가 Store에 읽혀오기 전이면 읽어준다.
      await store._actions['common/menu/fetchMenus'][0]();
    }
    const storeMenu = store.state.common.menu;
    // 메뉴접근권한 설정이 없으면 모든 메뉴에 권한이 있는 거다.
    if (storeMenu.allowedMenuIds.length == 0) return true;
    const primeMenuList = storeMenu.realtime.concat(storeMenu.analstatic, storeMenu.extraordinaries);
    const toMenu = this.findMenuRecursive(routeTo, primeMenuList);
    if (toMenu && storeMenu.allowedMenuIds.includes(toMenu.id) == false) {
      // 이동할 PATH에 해당하는 메뉴정보가 찾아졌고, 그 메뉴의 ID가 접근허용 목록에 들어있지 않으면
      // 접근을 거부하도록 false를 리턴한다.
      return false;
    }
    return true;
  }

  /**
   * Added.by.KWShin.2026.07.21.
   * 트리구조의 메뉴 목록에서 재귀적 호출을 이용하여
   * routeTo PATH에 매치되는 메뉴를 찾는다.
   */
  findMenuRecursive(routeTo, menuList) {
    for (const menu of menuList) {
      if (menu.url && routeTo.path.startsWith(menu.url)) {
        return menu;
      }
      if (menu.children && menu.children.length) {
        const m = this.findMenuRecursive(routeTo, menu.children);
        if (m) return m;
      }
    }
  }
}
